import { useMemo } from "react"

import type { FilePickerSource, NavigationItem, SharePointLibrary, SharePointSite } from "../types"
import type { UserDocument } from "@/api/models"

import { useDocuments } from "@/hooks/use-documents"

import {
  useGraphDriveChildren,
  useGraphFiles,
  useSharePointLibraries,
  useSharePointSites,
} from "./use-file-picker-queries"

interface UseFilePickerDataProps {
  tab: FilePickerSource
  open: boolean
  navStack: NavigationItem[]
  selectedSite: SharePointSite | null
  selectedLibrary: SharePointLibrary | null
}

export function useFilePickerData({
  tab,
  open,
  navStack,
  selectedSite,
  selectedLibrary,
}: UseFilePickerDataProps) {
  // Determine which queries to enable based on tab and navigation state
  const isLocal = tab === "local"
  const isDocuments = tab === "documents"
  const isOneDriveWithNav = tab === "oneDrive" && navStack.length > 0
  const isSharePointSites = tab === "sharePoint" && !selectedSite
  const isSharePointLibraries = tab === "sharePoint" && selectedSite && !selectedLibrary
  const isSharePointFiles = tab === "sharePoint" && selectedSite && selectedLibrary

  // Get parent item for OneDrive navigation
  const oneDriveParent = useMemo(
    () => (navStack.length > 0 ? navStack[navStack.length - 1] : null),
    [navStack]
  )

  // Get itemId for SharePoint navigation
  const sharePointItemId = useMemo(
    () => (navStack.length > 0 ? navStack[navStack.length - 1].itemId : "root"),
    [navStack]
  )

  // Graph files query (for oneDrive root, sharedWithMe, recent)
  const graphFilesQuery = useGraphFiles(
    tab,
    open &&
      !isLocal &&
      !isDocuments &&
      !isOneDriveWithNav &&
      !isSharePointSites &&
      !isSharePointLibraries &&
      !isSharePointFiles,
    undefined
  )
  const documentsQuery = useDocuments(undefined, open && isDocuments)

  // OneDrive drive children query
  const oneDriveChildrenQuery = useGraphDriveChildren(
    oneDriveParent?.driveId,
    oneDriveParent?.itemId,
    {
      enabled: open && isOneDriveWithNav,
      pageSize: undefined,
    }
  )

  // SharePoint sites query
  const sharePointSitesQuery = useSharePointSites(open && isSharePointSites, "auto", 50)

  // SharePoint libraries query
  const sharePointLibrariesQuery = useSharePointLibraries(
    selectedSite?.siteId,
    (open && isSharePointLibraries) || false,
    50
  )

  // SharePoint drive children query
  const sharePointChildrenQuery = useGraphDriveChildren(
    selectedLibrary?.driveId,
    sharePointItemId,
    {
      enabled: Boolean(open && isSharePointFiles),
      pageSize: 100,
    }
  )

  // Determine which data to return based on current state
  const files = useMemo(() => {
    if (isLocal) return []
    if (isDocuments) return []
    if (isOneDriveWithNav) return oneDriveChildrenQuery.data?.items || []
    if (isSharePointFiles) return sharePointChildrenQuery.data?.items || []
    return graphFilesQuery.data?.items || []
  }, [
    isLocal,
    isDocuments,
    isOneDriveWithNav,
    isSharePointFiles,
    oneDriveChildrenQuery.data,
    sharePointChildrenQuery.data,
    graphFilesQuery.data,
  ])

  const documents = useMemo(() => {
    if (!isDocuments) return [] as UserDocument[]
    return documentsQuery.data ?? []
  }, [isDocuments, documentsQuery.data])

  const sites = useMemo(() => {
    if (isSharePointSites) return sharePointSitesQuery.data?.items || []
    return []
  }, [isSharePointSites, sharePointSitesQuery.data])

  const libraries = useMemo(() => {
    if (isSharePointLibraries) return sharePointLibrariesQuery.data?.items || []
    return []
  }, [isSharePointLibraries, sharePointLibrariesQuery.data])

  const isLoading = useMemo(() => {
    if (isLocal) return false
    if (isDocuments) return documentsQuery.isLoading
    if (isOneDriveWithNav) return oneDriveChildrenQuery.isLoading
    if (isSharePointSites) return sharePointSitesQuery.isLoading
    if (isSharePointLibraries) return sharePointLibrariesQuery.isLoading
    if (isSharePointFiles) return sharePointChildrenQuery.isLoading
    return graphFilesQuery.isLoading
  }, [
    isLocal,
    isDocuments,
    isOneDriveWithNav,
    isSharePointSites,
    isSharePointLibraries,
    isSharePointFiles,
    documentsQuery.isLoading,
    oneDriveChildrenQuery.isLoading,
    sharePointSitesQuery.isLoading,
    sharePointLibrariesQuery.isLoading,
    sharePointChildrenQuery.isLoading,
    graphFilesQuery.isLoading,
  ])

  // The failure of whichever query feeds the current view, so the dialog can
  // say "no access to this site" instead of showing an empty list (#2874).
  const error = useMemo<Error | null>(() => {
    if (isLocal) return null
    if (isDocuments) return documentsQuery.error
    if (isOneDriveWithNav) return oneDriveChildrenQuery.error
    if (isSharePointSites) return sharePointSitesQuery.error
    if (isSharePointLibraries) return sharePointLibrariesQuery.error
    if (isSharePointFiles) return sharePointChildrenQuery.error
    return graphFilesQuery.error
  }, [
    isLocal,
    isDocuments,
    isOneDriveWithNav,
    isSharePointSites,
    isSharePointLibraries,
    isSharePointFiles,
    documentsQuery.error,
    oneDriveChildrenQuery.error,
    sharePointSitesQuery.error,
    sharePointLibrariesQuery.error,
    sharePointChildrenQuery.error,
    graphFilesQuery.error,
  ])

  return {
    files,
    documents,
    sites,
    libraries,
    isLoading,
    error,
  }
}
